import React, { useState } from "react";
import { InlineTextField } from "~/components/inline-text-field";
import {
  useInlineFormField,
  useInlineFormState,
} from "~/components/inline-fields-form";
import { EmployeeRole } from "~/refdata/useEmployeeSearch";
import { InlineEmployeePicker } from "~/visuals/organisms/InlineEmployeePicker";
import { InlineFieldOfficePicker } from "~/ProjectForm/InlineFieldOfficePicker";
import { InlineStateCountyPicker } from "~/ProjectForm/InlineStateCountyPicker";
import {
  FieldOffice,
  StateCounty,
  Customer,
  Employee,
  ProjectGroup,
  EogAfeTypeMutationInput,
} from "~/gql/types";
import { InlineDatePicker } from "~/visuals/organisms/InlineDatePicker";
import { Moment } from "moment";
import "./EditProjectComponents.scss";
import { ProjectRefData } from "~/refdata/sources";
import { InlineDivisionPicker } from "~/ProjectForm/InlineDivisionPicker";
import { InlineCorpLocationPicker } from "~/ProjectForm/InlineCorpLocationPicker";
import {
  EnumType,
  InlineEnumPicker,
} from "~/visuals/organisms/InlineEnumPicker";
import cn from "classnames";
import { ServiceDescription } from "~/refdata2/serviceDescriptions";
import { InlineServiceDescriptionPicker } from "~/ProjectForm/InlineServiceDescriptionPicker";
import { InlineCustomerPicker } from "~/visuals/organisms/InlineCustomerPicker";
import "~/visuals/organisms/BinaryPickerFormField/BinaryPickerFormField.scss";
import { CheckBox } from "~/components/checkbox";
import { InlineProjectGroupPicker } from "./InlineProjectGroupPicker";
import { InlineEogAfeTypePicker } from "./InlineEogAfeTypePicker";
import { GroupedFolder, SharedDrive } from "~/gql/types2";
import { InlineGroupedFolderPicker } from "./InlineGroupedFolderPicker";
import { InlineFolderDrivePicker } from "./InlineFolderDrivePicker";
import { Dropdown, DropdownProps } from "~/components/dropdown";
import { FolderRequestType } from "./types";
export { EditProjectOrigination } from "./editProjectOrigination";

export type BaseProps = {
  name: string;
};

type TextFieldProps = BaseProps & {
  textarea?: boolean;
  onFocus?: React.FocusEventHandler<any>;
  onBlur?: React.FocusEventHandler<any>;
};

export const EditProjectTextField: React.FC<TextFieldProps> = (props) => {
  const { name, textarea, onFocus, onBlur } = props;
  const { value, error, setError, updateValue, required, disabled } =
    useInlineFormField(name);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateValue(name, e.target.value);
  };

  const onBlurred = (e) => {
    if (required && !value) {
      setError(name, "Required");
    }
    onBlur && onBlur(e);
  };

  return (
    <InlineTextField
      {...{
        ...props,
        disabled,
        name,
        required,
        value: value ?? "",
        onChange,
        errorMessage: error ?? "",
        onBlur: onBlurred,
        onFocus,
        textarea,
      }}
    />
  );
};

export const EditProjectNotes: React.FC<BaseProps> = (props) => {
  const [focused, setFocused] = useState(false);
  const onFocus = () => {
    setFocused(true);
  };
  const onBlur = () => {
    setFocused(false);
  };

  const notesStyle = cn("project-notes", { focused });

  return (
    <div className={notesStyle}>
      <div className="scrollable-notes">
        <EditProjectTextField
          {...{
            ...props,
            textarea: true,
            onBlur,
            onFocus,
          }}
        />
      </div>
    </div>
  );
};

export const EditStateCounty: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<StateCounty>(props);

  return (
    <InlineStateCountyPicker
      {...{
        ...props,
        disabled,
        required,
        value,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

type EditEmployeeProps = BaseProps & {
  roleName: EmployeeRole;
};

export const EditEmployee: React.FC<EditEmployeeProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<Employee>(props);

  return (
    <InlineEmployeePicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
        roleName: props.roleName,
      }}
    />
  );
};

export const EditCustomer: React.FC<
  BaseProps & { includePending?: boolean; includeInProjectRequest?: boolean }
> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<Customer>(props);

  return (
    <InlineCustomerPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
        includePending: props.includePending,
        includeInProjectRequest: props.includeInProjectRequest,
      }}
    />
  );
};

export const EditGroupedFolder: React.FC<
  BaseProps & { groupedFolders: GroupedFolder[] }
> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<GroupedFolder>(props);

  return (
    <InlineGroupedFolderPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
        groupedFolders: props.groupedFolders,
      }}
    />
  );
};

export const EditFolderDrive: React.FC<
  BaseProps & { folderDrives: SharedDrive[] }
> = (props) => {
  const { name } = props;
  const { value, error, setError, updateValue, required, disabled } =
    useInlineFormField(name);

  const onChange = (drive: SharedDrive | null) => {
    updateValue(name, drive?.driveLetter ?? null);
  };

  const onBlur = () => {
    if (required && !value) {
      setError(name, "Required");
    }
  };

  const driveValue = props.folderDrives.find((x) => x.driveLetter == value);

  return (
    <InlineFolderDrivePicker
      {...{
        ...props,
        disabled,
        required,
        value: driveValue || null,
        onChange,
        submitError: error ?? "",
        onBlur,
        folderDrives: props.folderDrives,
      }}
    />
  );
};

export const EditOfficeCode: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<FieldOffice>(props);

  return (
    <InlineFieldOfficePicker
      {...{
        ...props,
        disabled,
        required,
        value,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

export const EditDate: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<Moment>(props);

  return (
    <div className="edit-project-date">
      <InlineDatePicker
        {...{
          ...props,
          disabled,
          value,
          required,
          onChange,
          submitError: error ?? "",
          onBlur,
        }}
      />
      &nbsp;
      {value ? `(${value.fromNow()})` : ""}
    </div>
  );
};

export const EditDivision: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<ProjectRefData>(props);

  return (
    <InlineDivisionPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

export const EditServiceDescription: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<ServiceDescription>(props);

  return (
    <InlineServiceDescriptionPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

export const EditCorpLocation: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<ProjectRefData>(props);

  return (
    <InlineCorpLocationPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

export const EditFolderType: React.FC<BaseProps> = (props) => {
  const { name } = props;
  const { value, error, setError, updateValue } = useInlineFormField(name);

  const onChange = (type: FolderRequestType | null) => {
    updateValue(name, type);
  };

  const onBlur = () => {
    if (!value) {
      setError(name, "Required");
    }
  };

  const items = Object.values(FolderRequestType);

  const dropdownProps: DropdownProps<FolderRequestType> = {
    items: items,
    formatItem: (x) => x,
    formatSelection: (x) => x,
    selectedItem: value,
    required: true,
    label: "Folder type",
    name: name,
    helperText: "",
    onSelected: onChange,
    onBlur: onBlur,
    isValid: !error,
  };

  return <Dropdown {...dropdownProps} />;
};

type EditEnumProps<E extends string> = BaseProps & {
  labels: Record<E, string>;
};

export function EditEnumType<E extends string>(
  props: EditEnumProps<E>
): ReturnType<React.FC> {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<EnumType>(props);

  return (
    <InlineEnumPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
}

export const EditCheckbox: React.FC<BaseProps> = (props) => {
  const { onChange, value, disabled } = useGetProps<boolean>(props);

  return (
    <CheckBox
      checked={value}
      onChange={() => onChange(!value)}
      disabled={disabled}
    />
  );
};

export const EditProjectBudget: React.FC<TextFieldProps> = (props) => {
  const { name } = props;
  const { value, error, setError, updateValue, required, disabled } =
    useInlineFormField(name);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateValue(name, e.target.value);
  };

  const onBlur = () => {
    if (required && !value) {
      setError(name, "Required");
    }
  };

  return (
    <div className="edit-project-budget">
      <span>$</span>
      <InlineTextField
        {...{
          ...props,
          disabled,
          name,
          required,
          value: value ?? "",
          onChange,
          errorMessage: error ?? "",
          onBlur,
          currency: true,
        }}
      />
    </div>
  );
};

export const EditProjectGroup: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<ProjectGroup>(props);

  const formState = useInlineFormState();

  const customerNumber = formState.fields.customer.value?.number;

  return (
    <InlineProjectGroupPicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
        customerNumber,
      }}
    />
  );
};

export const EditEogAfeType: React.FC<BaseProps> = (props) => {
  const { error, onChange, onBlur, value, required, disabled } =
    useGetProps<EogAfeTypeMutationInput>(props);

  return (
    <InlineEogAfeTypePicker
      {...{
        ...props,
        disabled,
        required,
        value: value || null,
        onChange,
        submitError: error ?? "",
        onBlur,
      }}
    />
  );
};

export function useGetProps<T>(props: BaseProps) {
  const { name } = props;
  const { value, error, setError, updateValue, required, disabled } =
    useInlineFormField(name);

  const onChange = (value: T | null) => {
    updateValue(name, value);
  };

  const onBlur = () => {
    if (required && (value === null || value === undefined || value === "")) {
      setError(name, "Required");
    }
  };

  return { onChange, onBlur, error, value, required, disabled, setError };
}
